import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';
import * as monthUtils from '@actual-app/core/shared/months';

import { SelectorCategorias } from '#components/mobile/budget/FiltrosCategorias';
import {
  AccionTexto,
  Tarjeta,
  VacioCompacto,
} from '#components/mobile/inicio/comunes';
import type { PropsWidget } from '#components/mobile/inicio/comunes';
import {
  HojaApuntarGasto,
  useGuardarGasto,
} from '#components/mobile/inicio/HojaApuntarGasto';
import { repetibles } from '#components/mobile/inicio/hoyPuedes';
import type { Hoy as DatosHoy } from '#components/mobile/inicio/hoyPuedes';
import { useGastosRecientes, useHoy } from '#components/mobile/inicio/useHoy';
import { Gavi } from '#components/mobile/travesia/Gavi';
import { BarraProgreso } from '#components/mobile/ui/BarraProgreso';
import { Boton } from '#components/mobile/ui/Boton';
import { separarEmoji } from '#components/mobile/ui/emoji';
import { Icono } from '#components/mobile/ui/Icono';
import { color, num, radio, TACTIL, texto } from '#components/mobile/ui/tokens';
import { PrivacyFilter } from '#components/PrivacyFilter';
import { useAccounts } from '#hooks/useAccounts';
import { useFormat } from '#hooks/useFormat';
import { usePayeesById } from '#hooks/usePayees';
import { registrarUso } from '#uso';

/**
 * «Hoy puedes gastar»: lo que queda en las categorías del día a día
 * repartido entre los días que faltan del mes, y el botón «Apuntar gasto»
 * (hoja de dos toques). Tocar «Categorías» elige cuáles cuentan.
 *
 * Tamaños: compacto = cifra y botón redondo «+»; normal = cifra, barra del
 * día, frase y «Apuntar gasto»; grande = además Gavi y «Repetir» (un toque
 * apunta el gasto sin abrir nada).
 */
export function Hoy({ tamano, month }: PropsWidget) {
  const { t } = useTranslation();
  const format = useFormat();
  const { hoy, ids, guardarIds, grupos, categorias } = useHoy(month);
  const gastos = useGastosRecientes();
  const [apuntando, setApuntando] = useState(false);
  const [eligiendo, setEligiendo] = useState(false);

  const gruposGasto = grupos.filter(g => !g.is_income);
  const hojas = (
    <>
      {apuntando && (
        <HojaApuntarGasto
          idsHoy={ids}
          categorias={categorias}
          grupos={grupos}
          gastos={gastos}
          onCerrar={() => setApuntando(false)}
        />
      )}
      {eligiendo && (
        <SelectorCategorias
          titulo={t('What counts for Today')}
          grupos={gruposGasto}
          seleccion={ids}
          onGuardar={nuevos => {
            guardarIds(nuevos);
            setEligiendo(false);
          }}
          onCerrar={() => setEligiendo(false)}
        />
      )}
    </>
  );
  const abrir = () => {
    registrarUso('gasto_rapido_abrir', { tipo: tamano });
    setApuntando(true);
  };
  const accion = (
    <AccionTexto onPress={() => setEligiendo(true)}>
      <Trans>Categories</Trans>
    </AccionTexto>
  );

  if (ids.length === 0) {
    return (
      <Tarjeta titulo={t('Today')} sinTarjeta data-testid="inicio-hoy">
        <VacioCompacto
          texto={t('Choose which categories count for today')}
          accion={t('Choose')}
          onAccion={() => setEligiendo(true)}
        />
        {hojas}
      </Tarjeta>
    );
  }

  const v = hoy ? vista(t, hoy) : null;
  const cifra = (
    <PrivacyFilter>
      <Text
        data-testid="hoy-cifra"
        style={{
          ...num,
          fontSize: tamano === 'compacto' ? 24 : 32,
          fontWeight: 800,
          letterSpacing: '-0.03em',
          color: v?.color ?? color.fg3,
        }}
      >
        {hoy ? format(Math.abs(v?.cifra ?? 0), 'financial') : '…'}
      </Text>
    </PrivacyFilter>
  );

  if (tamano === 'compacto') {
    return (
      <Tarjeta titulo={t('Today')} accion={accion} data-testid="inicio-hoy">
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text style={{ ...texto.secundario, color: color.fg2 }}>
              {v?.etiqueta ?? t('You can spend today')}
            </Text>
            {cifra}
          </View>
          <Button
            variant="bare"
            aria-label={t('Add expense')}
            onPress={abrir}
            style={{
              width: 52,
              height: 52,
              borderRadius: radio.pildora,
              backgroundColor: color.accent,
              color: color.accentInk,
            }}
          >
            <Icono nombre="plus" size={24} strokeWidth={2.6} />
          </Button>
        </View>
        {hojas}
      </Tarjeta>
    );
  }

  return (
    <Tarjeta titulo={t('Today')} accion={accion} data-testid="inicio-hoy">
      <View style={{ gap: 12 }}>
        <View
          style={{
            flexDirection: 'row',
            alignItems: 'flex-end',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <View style={{ minWidth: 0 }}>
            <Text
              style={{ ...texto.cuerpo, fontWeight: 600, color: color.fg2 }}
            >
              {v?.etiqueta ?? t('You can spend today')}
            </Text>
            {cifra}
          </View>
          {hoy && hoy.gastado > 0 && hoy.estado !== 'sin' && (
            <PrivacyFilter>
              <Text
                style={{
                  ...texto.secundario,
                  ...num,
                  color: color.fg2,
                  paddingBottom: 6,
                }}
              >
                {t('of {{amount}}', { amount: format(hoy.cuota, 'financial') })}
              </Text>
            </PrivacyFilter>
          )}
        </View>
        <BarraProgreso
          valor={hoy && hoy.cuota > 0 ? hoy.gastado / hoy.cuota : 0}
          color={v?.color}
          aria-label={t('Spent today')}
        />
        {hoy && v && (
          <Text style={{ ...texto.cuerpo, color: color.fg2 }}>
            {v.texto(format)}
          </Text>
        )}
        {tamano === 'grande' && hoy && v && (
          <View
            style={{
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              backgroundColor: color.surface2,
              borderRadius: radio.boton,
              padding: '8px 12px',
            }}
          >
            <Gavi
              ancho={36}
              estado={hoy.estado === 'encima' ? 'tapa' : 'reposo'}
            />
            <Text style={{ ...texto.cuerpo, fontWeight: 600, color: color.fg }}>
              {v.gavi}
            </Text>
          </View>
        )}
        {tamano === 'grande' && (
          <RepetirEnWidget gastos={gastos} categorias={categorias} ids={ids} />
        )}
        <Boton bloque onPress={abrir} style={{ gap: 8 }}>
          <Icono nombre="plus" size={18} strokeWidth={2.6} />
          <Trans>Add expense</Trans>
        </Boton>
      </View>
      {hojas}
    </Tarjeta>
  );
}

type Vista = {
  etiqueta: string;
  /** Cifra grande (céntimos; se enseña sin signo). */
  cifra: number;
  color: string;
  texto: (format: ReturnType<typeof useFormat>) => string;
  gavi: string;
};

function vista(
  t: ReturnType<typeof useTranslation>['t'],
  hoy: DatosHoy,
): Vista {
  switch (hoy.estado) {
    case 'sin':
      return {
        etiqueta: t('You can spend today'),
        cifra: 0,
        color: color.warn,
        texto: () => t('Nothing left in these categories this month.'),
        gavi: t('Cover them from the Plan and we set course again.'),
      };
    case 'encima':
      return {
        etiqueta: t('Today you are over by'),
        cifra: -hoy.resto,
        color: color.warn,
        texto: format =>
          hoy.manana == null
            ? t('Last day of the month: tomorrow starts a new one.')
            : t('Tomorrow you can spend {{amount}}.', {
                amount: format(hoy.manana, 'financial'),
              }),
        gavi: t('Oops, today got away from us a bit. Tomorrow we row gently.'),
      };
    case 'quedan':
      return {
        etiqueta: t('Left for today'),
        cifra: hoy.resto,
        color: color.accent,
        texto: format =>
          t('You have spent {{spent}} of {{amount}} today.', {
            spent: format(hoy.gastado, 'financial'),
            amount: format(hoy.cuota, 'financial'),
          }),
        gavi: t(
          "Going well. What you don't spend today, you'll have tomorrow.",
        ),
      };
    default:
      return {
        etiqueta: t('You can spend today'),
        cifra: hoy.cuota,
        color: color.accent,
        texto: format =>
          t('{{amount}} left for {{count}} days.', {
            amount: format(hoy.alEmpezar, 'financial'),
            count: hoy.dias,
          }),
        gavi: t('If you spend less today, tomorrow you will have more.'),
      };
  }
}

/** «Repetir» dentro del widget grande: un toque y queda apuntado. */
function RepetirEnWidget({
  gastos,
  categorias,
  ids,
}: {
  gastos: ReturnType<typeof useGastosRecientes>;
  categorias: ReturnType<typeof useHoy>['categorias'];
  /** Solo se repite lo de las categorías que cuentan para «Hoy». */
  ids: readonly string[];
}) {
  const { t } = useTranslation();
  const format = useFormat();
  const guardar = useGuardarGasto();
  const { data: cuentas = [] } = useAccounts();
  const { data: payeesById = {} } = usePayeesById();
  const [guardando, setGuardando] = useState(false);

  const abiertas = new Set(
    cuentas.filter(a => !a.closed && !a.offbudget).map(a => a.id),
  );
  const deHoy = new Set(ids);
  const porId = new Map(
    categorias.filter(c => deHoy.has(c.id)).map(c => [c.id, c]),
  );
  const lista = repetibles(gastos).filter(
    r => abiertas.has(r.account) && porId.has(r.category),
  );
  if (lista.length === 0) {
    return null;
  }
  return (
    <View style={{ gap: 8 }}>
      <Text style={{ ...texto.etiqueta, color: color.fg3 }}>
        <Trans>Repeat with one tap</Trans>
      </Text>
      <View
        style={{
          flexDirection: 'row',
          gap: 8,
          overflowX: 'auto',
          scrollbarWidth: 'none',
        }}
      >
        {lista.map(r => {
          const { emoji, resto } = separarEmoji(
            porId.get(r.category)?.name ?? '',
          );
          const sitio = (r.payee && payeesById[r.payee]?.name) || resto;
          return (
            <Button
              key={`${r.payee}|${r.category}|${r.importe}`}
              variant="bare"
              isDisabled={guardando}
              aria-label={t('Repeat {{name}} {{amount}}', {
                name: sitio,
                amount: format(r.importe, 'financial'),
              })}
              onPress={async () => {
                setGuardando(true);
                registrarUso('gasto_rapido', { tipo: 'repetir-widget' });
                await guardar(
                  {
                    cuenta: r.account,
                    fecha: monthUtils.currentDay(),
                    importe: r.importe,
                    categoria: r.category,
                    payee: r.payee,
                  },
                  resto,
                );
                setGuardando(false);
              }}
              style={{
                flexShrink: 0,
                minHeight: TACTIL,
                padding: '0 12px',
                borderRadius: radio.pildora,
                border: `1px solid ${color.line2}`,
                backgroundColor: color.surface,
                color: color.fg,
                gap: 6,
                ...texto.fila,
                fontSize: 14,
              }}
            >
              {emoji && <span aria-hidden>{emoji}</span>}
              <span>{sitio}</span>
              <span style={{ ...num, color: color.fg2, fontWeight: 600 }}>
                {format(r.importe, 'financial')}
              </span>
            </Button>
          );
        })}
      </View>
    </View>
  );
}
